import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Loader2, Save, User, Briefcase, MapPin, DollarSign, Calendar, Globe, Languages,
    FileText, Award, GraduationCap, ShieldCheck, Sparkles, IdCard, Link2, Video,
    Linkedin, Github, Twitter, PlusCircle, X, Zap, Trash2, AlertTriangle,
    Upload, Camera, Eye, Share2, Copy, Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { TrustBreakdownCard } from "@/components/TrustBreakdownCard";
import { AffindaResumeParser } from "@/components/AffindaResumeParser";
import { ProfilePhotoPicker } from "@/components/ProfilePhotoPicker";
import { Avatar } from "@/components/Avatar";
import { AnonymizedCard } from "@/components/AnonymizedCard";
import VideoIntroRecorder from "@/components/VideoIntroRecorder";
import VideoIntroPromptEditor from "@/components/VideoIntroPromptEditor";
import ProfileStrengthCard from "@/components/talent/ProfileStrengthCard";
import KnockoutAnswersSection from "@/components/talent/KnockoutAnswersSection";

// ---------------------------------------------------------------------------
// Controlled vocabularies — Feb 2026 iter 89
// Employers search + filter on these fields, so free-text ("LA" for
// Los Angeles, "part time" for Part-time, etc.) silently excluded talent
// from search results. Dropdowns fix that. The values used here match
// the exact strings the recruit search filters expect.
// ---------------------------------------------------------------------------
const ROLE_LEVELS = [
    "Individual Contributor", "Senior IC", "Lead / Staff", "Manager",
    "Director", "VP", "C-Level", "Founding Team", "Contractor / Freelance",
];
const EMPLOYMENT_TYPES = [
    "Full-time", "Part-time", "Contract", "Contract-to-hire",
    "Internship", "Temporary", "Freelance",
];
const AVAILABILITY_OPTIONS = [
    "Immediately", "Within 2 weeks", "Within 1 month",
    "Within 3 months", "Passively looking", "Not looking",
];
const REMOTE_OPTIONS = ["Remote-only", "Hybrid", "On-site", "Flexible"];
const WORK_AUTH_OPTIONS = [
    "US Citizen", "US Permanent Resident (Green Card)",
    "H-1B", "TN Visa", "O-1 Visa",
    "OPT / STEM OPT", "Requires Sponsorship",
    "EU Citizen / Right to Work (EU)",
    "UK Right to Work", "Canadian Citizen / PR",
    "Other — see notes",
];
// Top 60 US cities so the datalist covers most talent without paginating
// a giant list. Common abbreviations get normalized on save via the
// ``_normalizeCity`` helper below.
const US_CITY_HINTS = [
    "New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX",
    "Phoenix, AZ", "Philadelphia, PA", "San Antonio, TX", "San Diego, CA",
    "Dallas, TX", "San Jose, CA", "Austin, TX", "Jacksonville, FL",
    "Fort Worth, TX", "Columbus, OH", "Indianapolis, IN", "Charlotte, NC",
    "San Francisco, CA", "Seattle, WA", "Denver, CO", "Washington, DC",
    "Boston, MA", "Nashville, TN", "El Paso, TX", "Detroit, MI",
    "Oklahoma City, OK", "Portland, OR", "Las Vegas, NV", "Memphis, TN",
    "Louisville, KY", "Baltimore, MD", "Milwaukee, WI", "Albuquerque, NM",
    "Tucson, AZ", "Fresno, CA", "Sacramento, CA", "Kansas City, MO",
    "Atlanta, GA", "Miami, FL", "Raleigh, NC", "Omaha, NE",
    "Long Beach, CA", "Minneapolis, MN", "Colorado Springs, CO", "Tampa, FL",
    "Arlington, TX", "New Orleans, LA", "Cleveland, OH", "Honolulu, HI",
    "Anaheim, CA", "Bakersfield, CA", "Aurora, CO", "Santa Ana, CA",
    "Riverside, CA", "Corpus Christi, TX", "Lexington, KY", "Anchorage, AK",
    "Stockton, CA", "Cincinnati, OH", "Saint Paul, MN", "Toledo, OH",
];
// Normalize a handful of common abbreviations users type. Employers
// searching for "Los Angeles" won't match "LA" — normalize on save.
const CITY_NORMALIZATIONS = {
    "la":  "Los Angeles, CA",
    "lax": "Los Angeles, CA",
    "sf":  "San Francisco, CA",
    "sfo": "San Francisco, CA",
    "nyc": "New York, NY",
    "ny":  "New York, NY",
    "dc":  "Washington, DC",
    "atl": "Atlanta, GA",
    "chi": "Chicago, IL",
    "sea": "Seattle, WA",
    "pdx": "Portland, OR",
    "phl": "Philadelphia, PA",
    "bos": "Boston, MA",
    "hou": "Houston, TX",
    "aus": "Austin, TX",
    "dal": "Dallas, TX",
    "vegas": "Las Vegas, NV",
    "mia": "Miami, FL",
};
const normalizeCity = (v) => {
    const k = (v || "").trim().toLowerCase();
    if (!k) return v;
    return CITY_NORMALIZATIONS[k] || v;
};

// Reusable dropdown that stays keyboard + screen-reader friendly. Uses
// the native <select> so it inherits the platform look on mobile
// (which matters for a talent-facing form).
const Dropdown = ({ label, value, onChange, options, placeholder, testId, allowEmpty = true }) => (
    <div>
        <Label>{label}</Label>
        <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-2 w-full bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        >
            {allowEmpty && <option value="">{placeholder || "Select…"}</option>}
            {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
            ))}
        </select>
    </div>
);

const Section = ({ icon: Icon, title, desc, children, testId, right }) => (
    <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.3 }}
        data-testid={testId}
        className="card-soft p-6 scroll-mt-24"
    >
        <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                    <Icon size={18} />
                </div>
                <div>
                    <h2 className="font-heading font-semibold text-lg text-foreground">{title}</h2>
                    {desc && <p className="text-sm text-muted-foreground mt-1">{desc}</p>}
                </div>
            </div>
            {right}
        </div>
        <div className="mt-5">{children}</div>
    </motion.section>
);

/** Simple chip list editor: array of strings */
const ChipsEditor = ({ items = [], onChange, placeholder, testId }) => {
    const [draft, setDraft] = useState("");
    const add = () => {
        const v = draft.trim();
        if (!v) return;
        if (items.includes(v)) return setDraft("");
        onChange([...items, v]);
        setDraft("");
    };
    return (
        <div>
            <div className="flex flex-wrap gap-1.5">
                {items.map((s, i) => (
                    <Badge key={i} variant="outline" data-testid={`${testId}-chip`} className="text-xs pl-2 pr-1 py-1 flex items-center gap-1">
                        {s}
                        <button
                            type="button"
                            onClick={() => onChange(items.filter((_, j) => j !== i))}
                            className="text-muted-foreground hover:text-destructive ml-0.5"
                            aria-label={`Remove ${s}`}
                        >
                            <X size={11} />
                        </button>
                    </Badge>
                ))}
            </div>
            <div className="mt-3 flex gap-2">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder={placeholder}
                    data-testid={`${testId}-input`}
                    className="h-9"
                />
                <button type="button" onClick={add} data-testid={`${testId}-add`} className="btn-outline text-xs px-3 py-2 shrink-0">
                    <PlusCircle size={13} className="mr-1.5" /> Add
                </button>
            </div>
        </div>
    );
};

const TalentPassport = () => {
    const [pp, setPp] = useState(null);
    const [saving, setSaving] = useState(false);
    const [boost, setBoost] = useState(null);
    const [boostBusy, setBoostBusy] = useState(false);
    const [params] = useSearchParams();
    const navigate = useNavigate();
    // Danger-zone deletion state — talent's own passport wipe.
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    // Iter 94 — Marketplace preview state.
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    // Iter 95 — Shareable preview link state.
    const [shareUrl, setShareUrl] = useState("");
    const [shareBusy, setShareBusy] = useState(false);
    const [shareCopied, setShareCopied] = useState(false);

    const openPreview = async () => {
        setPreviewOpen(true);
        setPreviewLoading(true);
        try {
            const { data } = await api.get("/professional/passport/marketplace-preview");
            setPreviewData(data);
        } catch (err) {
            toast.error(formatApiError(err));
            setPreviewOpen(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const mintShareLink = async () => {
        setShareBusy(true);
        try {
            const { data } = await api.post("/professional/passport/share-link");
            const url = `${window.location.origin}/preview/${data.token}`;
            setShareUrl(url);
            try {
                await navigator.clipboard.writeText(url);
                setShareCopied(true);
                toast.success("Share link copied to clipboard.");
                setTimeout(() => setShareCopied(false), 3000);
            } catch { /* clipboard blocked — user can still copy manually */ }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setShareBusy(false);
        }
    };

    const revokeShareLink = async () => {
        setShareBusy(true);
        try {
            await api.delete("/professional/passport/share-link");
            setShareUrl("");
            setShareCopied(false);
            toast.success("Share link revoked.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setShareBusy(false);
        }
    };

    // Live client-side completion — mirrors the exact scoring formula in
    // ``backend/routers/professional.py`` (_completion). Recomputed on
    // every keystroke so the meter animates as the user fills fields,
    // instead of the number only updating after a Save round-trip.
    //
    // MUST match the backend weights (required 85% / bonus 15%, 5 bonus
    // fields max) or the number would tick down on save. If the backend
    // list moves, mirror it here.
    const completionLive = useMemo(() => {
        if (!pp) return 0;
        const REQ = ["name", "email", "phone", "city", "desired_position",
            "years_experience", "skills", "experience", "education",
            "work_authorization", "desired_salary", "availability"];
        const BONUS = ["linkedin_url", "profile_photo_url", "ai_summary",
            "resume_text", "certifications", "preferred_roles", "remote_preferences",
            "portfolio_url", "video_intro_url", "references",
            "github_url", "website_url", "languages_spoken", "job_type_preferences",
            "twitter_url"];
        const filled = (v) => {
            if (v === null || v === undefined) return false;
            if (Array.isArray(v)) return v.length > 0;
            if (typeof v === "object") return Object.keys(v).length > 0;
            if (typeof v === "string") return v.trim().length > 0;
            if (typeof v === "number") return true;
            return !!v;
        };
        const reqFilled = REQ.filter((f) => filled(pp[f])).length;
        const bonusFilled = BONUS.filter((f) => filled(pp[f])).length;
        const reqPct = (reqFilled / REQ.length) * 85;
        const bonusPct = Math.min(bonusFilled, 5) * 3;
        return Math.round(Math.min(100, reqPct + bonusPct));
    }, [pp]);

    // "Search-visible" threshold matches backend visibility bands:
    // >=40% = Medium visibility; <40% = Low (buried in the marketplace).
    // We coach toward crossing 40% first, then toward 70% for High.
    const nudgeCopy = useMemo(() => {
        if (!pp) return null;
        if (completionLive >= 70) {
            return { tone: "high", copy: "You're High visibility to employers — 🔥 keep it that way." };
        }
        if (completionLive >= 40) {
            const gap = 70 - completionLive;
            return { tone: "medium", copy: `You're search-visible — add a few more details to reach High visibility (${gap}% to go).` };
        }
        const gap = 40 - completionLive;
        return { tone: "low", copy: `You're ${gap}% away from being search-visible to employers. Fill a few more fields to appear in their results.` };
    }, [completionLive, pp]);

    const loadBoost = useCallback(async () => {
        try {
            const { data } = await api.get("/talent/passport-boost/status");
            setBoost(data);
        } catch (err) {
            // Boost tile is an upsell — if the endpoint errors we intentionally
            // don't render the card at all (fail-close on the upsell). Log for
            // devs so a broken endpoint doesn't stay silently invisible.
            console.warn("[TalentPassport] loadBoost failed:", err?.message || err);
        }
    }, []);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/professional/passport");
            setPp(data);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); loadBoost(); }, [load, loadBoost]);

    // Handle Stripe redirect back after boosting
    useEffect(() => {
        if (params.get("boosted")) {
            toast.success("Boost active — your passport is now pinned to the top for 14 days.");
            loadBoost();
        } else if (params.get("boost_canceled")) {
            toast.info("Boost canceled. You can try again anytime.");
        }
    }, [params, loadBoost]);

    const startBoost = async () => {
        setBoostBusy(true);
        try {
            const { data } = await api.post("/talent/passport-boost");
            if (data?.url) window.location.href = data.url;
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBoostBusy(false); }
    };

    const upd = (patch) => setPp((x) => ({ ...x, ...patch }));

    const doDelete = async () => {
        try {
            setDeleting(true);
            const { data } = await api.delete("/professional/passport");
            // The API is now a two-phase soft-delete with a 24h undo window.
            // Response returns `soft_deleted_rows` (>=1 when a live row was
            // flagged) plus `hard_delete_at`. Older UI branch here checked
            // `deleted_rows` which never exists — always dropped to the
            // "nothing to delete" fallback, misleading users.
            if (data?.soft_deleted_rows) {
                toast.success(
                    "Passport marked for deletion — you have 24 hours to undo from your dashboard.",
                );
            } else {
                toast.success("You didn't have a saved passport — nothing to delete.");
            }
            setDeleteOpen(false); setDeleteConfirmText("");
            // Send them back to the dashboard — the undo banner surfaces
            // there while the grace window is open.
            navigate("/professional/dashboard", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeleting(false);
        }
    };

    const save = async () => {
        if (!pp) return;
        try {
            setSaving(true);
            const payload = {
                name: pp.name, phone: pp.phone, city: pp.city,
                desired_position: pp.desired_position, years_experience: pp.years_experience,
                languages_spoken: pp.languages_spoken, linkedin_url: pp.linkedin_url,
                work_authorization: pp.work_authorization, desired_salary: pp.desired_salary,
                availability: pp.availability, job_type_preferences: pp.job_type_preferences,
                profile_photo_url: pp.profile_photo_url, ai_summary: pp.ai_summary,
                skills: pp.skills, experience: pp.experience, education: pp.education,
                certifications: pp.certifications, preferred_roles: pp.preferred_roles,
                remote_preferences: pp.remote_preferences, references: pp.references,
                portfolio_url: pp.portfolio_url, twitter_url: pp.twitter_url,
                github_url: pp.github_url, website_url: pp.website_url,
            };
            const { data } = await api.patch("/professional/passport", payload);
            toast.success(`Saved — ${data.updated?.length || 0} field${data.updated?.length === 1 ? "" : "s"} updated.`);
            // Kick the ProfileStrengthCard to refetch so the score updates
            // instantly (card listens on this window event — iter 142).
            window.dispatchEvent(new Event("passport-strength-refresh"));
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    if (!pp) return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="talent-passport-page" className="max-w-4xl">
            {/* Header + save */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-8 sticky top-16 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-background/85 backdrop-blur z-20 border-b border-border">
                <div>
                    <p className="overline">Your identity</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Talent Passport™
                    </h1>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right hidden sm:block">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Completion</div>
                        <div data-testid="passport-completion-live" className="mono-score text-xl font-bold text-emerald-600 dark:text-emerald-400 transition-all">{completionLive}%</div>
                    </div>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="passport-save"
                        className="btn-green text-sm disabled:opacity-60"
                    >
                        {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving</> : <><Save size={14} className="mr-2" /> Save Passport</>}
                    </button>
                </div>
            </div>

            <div className="space-y-5">
                {/* iter 143 — Profile Strength card mounted on the editor so
                    the score updates live as the user edits (save triggers a
                    `passport-strength-refresh` window event). */}
                <ProfileStrengthCard />
                {/* Iter 90 — Live passport-completeness meter. Animates as the
                    user fills fields (no Save round-trip needed) and tells them
                    the exact search-visibility gap so completion feels
                    actionable, not decorative. */}
                <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    data-testid="passport-completeness-meter"
                    className={`card-soft p-5 border ${
                        nudgeCopy?.tone === "high"
                            ? "border-emerald-500/40 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-500/10 dark:to-transparent"
                            : nudgeCopy?.tone === "medium"
                                ? "border-sky-500/40 bg-gradient-to-br from-sky-50 to-white dark:from-sky-500/10 dark:to-transparent"
                                : "border-amber-500/40 bg-gradient-to-br from-amber-50 to-white dark:from-amber-500/10 dark:to-transparent"
                    }`}
                >
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                                Passport Completeness
                            </div>
                            <div className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                                {completionLive}% complete
                            </div>
                            {nudgeCopy && (
                                <div
                                    data-testid={`passport-completeness-nudge-${nudgeCopy.tone}`}
                                    className={`text-sm mt-1.5 font-medium ${
                                        nudgeCopy.tone === "high"
                                            ? "text-emerald-700 dark:text-emerald-400"
                                            : nudgeCopy.tone === "medium"
                                                ? "text-sky-700 dark:text-sky-400"
                                                : "text-amber-700 dark:text-amber-500"
                                    }`}
                                >
                                    {nudgeCopy.copy}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1.5">
                                {[0, 40, 70, 100].map((mark) => (
                                    <span
                                        key={mark}
                                        className={`w-1.5 h-1.5 rounded-full ${
                                            completionLive >= mark
                                                ? (mark >= 70 ? "bg-emerald-500" : mark >= 40 ? "bg-sky-500" : "bg-amber-500")
                                                : "bg-muted"
                                        }`}
                                        title={mark === 0 ? "Start" : mark === 40 ? "Search-visible" : mark === 70 ? "High visibility" : "Complete"}
                                    />
                                ))}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                                {completionLive >= 70 ? "High" : completionLive >= 40 ? "Medium" : "Low"} visibility
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${completionLive}%` }}
                            transition={{ type: "spring", stiffness: 100, damping: 20 }}
                            className={`h-full rounded-full ${
                                completionLive >= 70
                                    ? "bg-gradient-to-r from-emerald-400 to-emerald-600"
                                    : completionLive >= 40
                                        ? "bg-gradient-to-r from-sky-400 to-sky-600"
                                        : "bg-gradient-to-r from-amber-400 to-amber-600"
                            }`}
                        />
                    </div>
                    <div className="mt-3 flex justify-end">
                        <button
                            type="button"
                            onClick={openPreview}
                            data-testid="passport-preview-btn"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors"
                        >
                            <Eye size={12} /> Preview as employer
                        </button>
                    </div>
                </motion.div>

                {pp.email && (
                    <TrustBreakdownCard email={pp.email} />
                )}

                {/* Passport Boost — paid pin to top of employer marketplace. */}
                {boost && (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        data-testid="passport-boost-card"
                        className={`card-soft p-5 ${boost.active ? "bg-gradient-to-br from-amber-50 to-white dark:from-amber-500/10 dark:to-transparent border-amber-500/40" : ""}`}
                    >
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <div>
                                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                                    <Zap size={14} /> <p className="overline">Passport Boost</p>
                                    {boost.active && (
                                        <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-0 text-[10px]" data-testid="boost-badge-active">Active</Badge>
                                    )}
                                </div>
                                <h2 className="mt-2 font-heading font-semibold text-lg text-foreground">
                                    {boost.active ? "You're boosted right now" : "Get in front of hiring managers first"}
                                </h2>
                                <p className="mt-1 text-sm text-muted-foreground max-w-md">
                                    {boost.active
                                        ? <>Your passport sits at the top of employer searches until <strong className="text-foreground">{new Date(boost.boost_until).toLocaleDateString()}</strong>.</>
                                        : <>$29 pins your passport to the top of the marketplace for {boost.days} days — recruiters see you before anyone else.</>}
                                </p>
                            </div>
                            {!boost.active && (
                                <button
                                    onClick={startBoost}
                                    disabled={boostBusy}
                                    data-testid="passport-boost-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-amber-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-amber-700 disabled:opacity-60 shrink-0"
                                >
                                    {boostBusy ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                                    Boost for 14 days — $29
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}

                <Section
                    icon={IdCard}
                    title="Overview"
                    desc="How employers first see you. Keep it concise and clear."
                    testId="passport-overview"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label>Full name</Label>
                            <Input value={pp.name || ""} onChange={(e) => upd({ name: e.target.value })} data-testid="pp-name" className="mt-2" />
                        </div>
                        <div>
                            <Label>Email (read-only)</Label>
                            <Input value={pp.email || ""} disabled className="mt-2 opacity-60" />
                        </div>
                        <ProfilePhotoPicker
                            url={pp.profile_photo_url || ""}
                            onChange={(url) => upd({ profile_photo_url: url })}
                            endpoint="/professional/profile-photo"
                            testIdPrefix="pp-photo"
                            name={pp.name || ""}
                            email={pp.email || ""}
                        />
                        <div>
                            <Label>Phone</Label>
                            <Input value={pp.phone || ""} onChange={(e) => upd({ phone: e.target.value })} data-testid="pp-phone" className="mt-2" />
                        </div>
                    </div>
                    <div className="mt-4">
                        <Label>AI Summary <span className="text-xs text-muted-foreground font-normal">— your 2–3 sentence elevator pitch</span></Label>
                        <Textarea
                            rows={3}
                            value={pp.ai_summary || ""}
                            onChange={(e) => upd({ ai_summary: e.target.value })}
                            data-testid="pp-summary"
                            className="mt-2"
                            placeholder="Product-led designer with 8 years shaping fintech + healthcare experiences. Comfortable owning end-to-end delivery, from user research to launch."
                        />
                    </div>
                </Section>

                <Section icon={Briefcase} title="Career preferences" desc="What role, where, when, how much." testId="passport-career">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label>Desired position</Label>
                            <Input value={pp.desired_position || ""} onChange={(e) => upd({ desired_position: e.target.value })} data-testid="pp-position" className="mt-2" />
                        </div>
                        <div>
                            <Label>Years of experience</Label>
                            <Input value={pp.years_experience || ""} onChange={(e) => upd({ years_experience: e.target.value })} className="mt-2" />
                        </div>
                        <Dropdown
                            label="Preferred role level"
                            value={pp.preferred_roles}
                            onChange={(v) => upd({ preferred_roles: v })}
                            options={ROLE_LEVELS}
                            placeholder="Select role level…"
                            testId="pp-role-level"
                        />
                        <Dropdown
                            label="Employment type"
                            value={pp.job_type_preferences}
                            onChange={(v) => upd({ job_type_preferences: v })}
                            options={EMPLOYMENT_TYPES}
                            placeholder="Select employment type…"
                            testId="pp-employment-type"
                        />
                        <div>
                            <Label>Desired salary</Label>
                            <Input value={pp.desired_salary || ""} onChange={(e) => upd({ desired_salary: e.target.value })} placeholder="$140k–$170k" className="mt-2" data-testid="pp-salary" />
                        </div>
                        <Dropdown
                            label="Availability"
                            value={pp.availability}
                            onChange={(v) => upd({ availability: v })}
                            options={AVAILABILITY_OPTIONS}
                            placeholder="Select availability…"
                            testId="pp-availability"
                        />
                    </div>
                </Section>

                <Section icon={MapPin} title="Location & remote" desc="Where you're based and how you work." testId="passport-location">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label>City</Label>
                            <Input
                                value={pp.city || ""}
                                onChange={(e) => upd({ city: e.target.value })}
                                onBlur={(e) => {
                                    // Normalize common abbreviations on blur (LA → Los Angeles, CA)
                                    // so employers searching by full city name can find this talent.
                                    const n = normalizeCity(e.target.value);
                                    if (n !== e.target.value) upd({ city: n });
                                }}
                                list="pp-city-hints"
                                placeholder="Los Angeles, CA"
                                className="mt-2"
                                data-testid="pp-city"
                            />
                            <datalist id="pp-city-hints">
                                {US_CITY_HINTS.map((c) => <option key={c} value={c} />)}
                            </datalist>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Use full city name for best search visibility (e.g. &quot;Los Angeles, CA&quot; — not &quot;LA&quot;).
                            </p>
                        </div>
                        <Dropdown
                            label="Remote preferences"
                            value={pp.remote_preferences}
                            onChange={(v) => upd({ remote_preferences: v })}
                            options={REMOTE_OPTIONS}
                            placeholder="Select preference…"
                            testId="pp-remote"
                        />
                        <Dropdown
                            label="Work authorization"
                            value={pp.work_authorization}
                            onChange={(v) => upd({ work_authorization: v })}
                            options={WORK_AUTH_OPTIONS}
                            placeholder="Select status…"
                            testId="pp-work-auth"
                        />
                        <div>
                            <Label className="flex items-center gap-2"><Languages size={13} /> Languages</Label>
                            <Input value={pp.languages_spoken || ""} onChange={(e) => upd({ languages_spoken: e.target.value })} placeholder="English (native), Spanish (professional)" className="mt-2" data-testid="pp-languages" />
                        </div>
                    </div>
                </Section>

                <Section
                    icon={Sparkles}
                    title="AI resume parse"
                    desc="Upload your resume once — we'll auto-fill the sections below so you don't have to retype."
                    testId="passport-affinda"
                >
                    <AffindaResumeParser
                        currentSkills={Array.isArray(pp.skills) ? pp.skills : (pp.skills ? String(pp.skills).split(",").map((s) => s.trim()) : [])}
                        currentExperience={Array.isArray(pp.experience) ? pp.experience : (pp.experience ? [String(pp.experience)] : [])}
                        currentEducation={Array.isArray(pp.education) ? pp.education : (pp.education ? [String(pp.education)] : [])}
                        currentName={pp.name || ""}
                        currentPhone={pp.phone || ""}
                        onMergeSkills={(items) => upd({ skills: items })}
                        onMergeExperience={(items) => upd({ experience: items })}
                        onMergeEducation={(items) => upd({ education: items })}
                        onAutoFillPersonal={(patch) => upd(patch)}
                    />
                </Section>

                <Section icon={Sparkles} title="Skills" desc="What you're great at. Press Enter to add each one." testId="passport-skills">
                    <ChipsEditor
                        items={Array.isArray(pp.skills) ? pp.skills : (pp.skills ? String(pp.skills).split(",").map((s) => s.trim()) : [])}
                        onChange={(items) => upd({ skills: items })}
                        placeholder="Product design, Figma, User research…"
                        testId="pp-skills"
                    />
                </Section>

                <Section icon={Briefcase} title="Experience" desc="Your career trajectory in reverse order." testId="passport-experience">
                    <ChipsEditor
                        items={Array.isArray(pp.experience)
                            ? pp.experience.map((it) => (typeof it === "string" ? it : [it.title, it.company, it.years].filter(Boolean).join(" · ")))
                            : (pp.experience ? [String(pp.experience)] : [])}
                        onChange={(items) => upd({ experience: items })}
                        placeholder="e.g. Senior PD @ Stripe · 2022–Now"
                        testId="pp-experience"
                    />
                </Section>

                <Section icon={GraduationCap} title="Education" desc="Degrees, bootcamps, meaningful programs." testId="passport-education">
                    <ChipsEditor
                        items={Array.isArray(pp.education)
                            ? pp.education.map((it) => (typeof it === "string" ? it : [it.degree, it.school, it.year].filter(Boolean).join(" · ")))
                            : (pp.education ? [String(pp.education)] : [])}
                        onChange={(items) => upd({ education: items })}
                        placeholder="e.g. B.S. Computer Science, UCLA"
                        testId="pp-education"
                    />
                </Section>

                <Section icon={Award} title="Certifications" desc="Verified credentials you've earned." testId="passport-certs">
                    <ChipsEditor
                        items={Array.isArray(pp.certifications) ? pp.certifications : []}
                        onChange={(items) => upd({ certifications: items })}
                        placeholder="e.g. AWS Solutions Architect, PMP"
                        testId="pp-certs"
                    />
                </Section>

                <Section icon={Video} title="Video Introduction" desc="A 60-second intro that beats a resume." testId="passport-video">
                    <VideoIntroRecorder />
                    <div className="mt-3">
                        <VideoIntroPromptEditor />
                    </div>
                </Section>

                <Section icon={ShieldCheck} title="Trust & References" desc="Employers pay premium for verified references." testId="passport-references">
                    <ChipsEditor
                        items={Array.isArray(pp.references) ? pp.references : []}
                        onChange={(items) => upd({ references: items })}
                        placeholder="e.g. Sarah Chen · Design Lead @ Stripe · sarah@stripe.com"
                        testId="pp-references"
                    />
                </Section>

                <Section icon={Link2} title="Portfolio & social" desc="Where employers can dig deeper." testId="passport-links">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="flex items-center gap-2"><Linkedin size={13} /> LinkedIn</Label>
                            <Input value={pp.linkedin_url || ""} onChange={(e) => upd({ linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/…" className="mt-2" />
                        </div>
                        <div>
                            <Label className="flex items-center gap-2"><Globe size={13} /> Portfolio / website</Label>
                            <Input value={pp.portfolio_url || pp.website_url || ""} onChange={(e) => upd({ portfolio_url: e.target.value })} placeholder="https://…" className="mt-2" />
                        </div>
                        <div>
                            <Label className="flex items-center gap-2"><Github size={13} /> GitHub</Label>
                            <Input value={pp.github_url || ""} onChange={(e) => upd({ github_url: e.target.value })} placeholder="https://github.com/…" className="mt-2" />
                        </div>
                        <div>
                            <Label className="flex items-center gap-2"><Twitter size={13} /> X / Twitter</Label>
                            <Input value={pp.twitter_url || ""} onChange={(e) => upd({ twitter_url: e.target.value })} placeholder="https://x.com/…" className="mt-2" />
                        </div>
                    </div>
                </Section>

                {/* iter-170 — Pre-fill answers to common screening questions
                    so this passport exits the "Didn't meet filters" collapsed
                    bucket on roles asking those questions. */}
                <div className="mt-6">
                    <KnockoutAnswersSection />
                </div>

                <div className="flex justify-end">
                    <button onClick={save} disabled={saving} data-testid="passport-save-bottom" className="btn-green">
                        {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : <><Save size={14} className="mr-2" /> Save Passport</>}
                    </button>
                </div>

                {/* Danger zone — GDPR self-serve delete. Kept intentionally
                    quiet (muted card at the bottom) so it doesn't tempt an
                    accidental click, but discoverable enough that a user
                    exercising their right-to-delete can find it. */}
                {pp?.has_passport && (
                    <section
                        data-testid="passport-danger-zone"
                        className="mt-10 rounded-xl border border-rose-500/20 bg-rose-500/[0.04] p-5"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-rose-500/15 text-rose-600 flex items-center justify-center shrink-0">
                                <AlertTriangle size={16} />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-heading font-semibold text-sm text-foreground">Delete my passport</h3>
                                <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                                    Removes your talent passport permanently — headline, experience, education, skills,
                                    verifications, everything you&apos;ve entered here. Your account, past applications,
                                    and login credentials are NOT touched. You can rebuild a fresh passport any time.
                                </p>
                            </div>
                            <button
                                onClick={() => setDeleteOpen(true)}
                                className="text-xs px-3 py-2 rounded border border-rose-500/40 text-rose-600 hover:bg-rose-500/10 inline-flex items-center gap-1.5 shrink-0"
                                data-testid="passport-delete-open"
                            >
                                <Trash2 size={12} /> Delete my passport
                            </button>
                        </div>
                    </section>
                )}

                <Dialog
                    open={deleteOpen}
                    onOpenChange={(v) => { if (!v) setDeleteConfirmText(""); setDeleteOpen(v); }}
                >
                    <DialogContent data-testid="passport-delete-dialog">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-rose-600">
                                <AlertTriangle size={16} /> Delete your passport?
                            </DialogTitle>
                            <DialogDescription className="pt-1 space-y-2">
                                <p>This permanently removes your talent profile from Simonara — recruiter searches, match feeds, everything.</p>
                                <p className="text-xs">
                                    Type <b className="font-mono">DELETE</b> below to confirm. Your login, past applications,
                                    and any messages you&apos;ve exchanged stay intact.
                                </p>
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-2">
                            <Input
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder="Type DELETE to confirm"
                                data-testid="passport-delete-confirm-input"
                                autoFocus
                                className="font-mono"
                            />
                        </div>
                        <DialogFooter>
                            <button
                                type="button"
                                onClick={() => { setDeleteOpen(false); setDeleteConfirmText(""); }}
                                className="btn-outline text-sm px-4 py-2"
                                data-testid="passport-delete-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={doDelete}
                                disabled={deleting || deleteConfirmText !== "DELETE"}
                                className="text-sm px-4 py-2 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 inline-flex items-center gap-2"
                                data-testid="passport-delete-submit"
                            >
                                {deleting ? <><Loader2 size={13} className="animate-spin" /> Deleting…</> : <><Trash2 size={13} /> Delete permanently</>}
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Iter 94 — Marketplace preview modal. Renders the
                    talent's own passport shaped by the SAME server-side
                    anonymization shaper the employer marketplace uses,
                    so what they see here is byte-for-byte what employers
                    see. Helps talent understand the anonymization
                    contract (no name, no email, no phone). */}
                <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                    <DialogContent data-testid="passport-preview-dialog" className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Eye size={16} /> How employers see you
                            </DialogTitle>
                            <DialogDescription className="pt-1">
                                This is your exact anonymized card in the Talent Marketplace.
                                Your name, email, phone, and resume text are never shown —
                                employers only see initials + role/city/skills + verifications.
                            </DialogDescription>
                        </DialogHeader>
                        {previewLoading && (
                            <div className="py-8 flex justify-center" data-testid="passport-preview-loading">
                                <Loader2 size={20} className="animate-spin text-violet-500" />
                            </div>
                        )}
                        {!previewLoading && previewData && !previewData.card && (
                            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="passport-preview-empty">
                                You don&apos;t have a passport yet — save the form once to create your card.
                            </div>
                        )}
                        {!previewLoading && previewData?.card && (
                            <>
                                {previewData.reason === "missing_desired_position" && (
                                    <div
                                        data-testid="passport-preview-hidden-warning"
                                        className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2"
                                    >
                                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                        <span>
                                            You&apos;re <strong>NOT</strong> in the marketplace yet — passports
                                            without a Desired position are hidden from employer search.
                                            Fill the &ldquo;Desired role/title&rdquo; field above to appear.
                                        </span>
                                    </div>
                                )}
                                {/* Anonymized card — mirror of the Marketplace list item */}
                                <AnonymizedCard card={previewData.card} testId="passport-preview-card" />
                                <div className="text-[11px] text-muted-foreground mt-2">
                                    ✓ No name, email, or phone shown — employers reach out through Simonara admin, and you consent before any identity is revealed.{" "}
                                    <a
                                        href="/how-it-works"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline hover:text-foreground"
                                    >
                                        How this works →
                                    </a>
                                </div>

                                {/* Iter 95 — Shareable preview link */}
                                <div className="mt-4 pt-4 border-t border-border" data-testid="passport-share-block">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Share2 size={13} className="text-sky-600 dark:text-sky-400" />
                                        <span className="text-xs font-semibold text-foreground">Share this preview</span>
                                    </div>
                                    <p className="text-[11px] text-muted-foreground mb-2">
                                        Get a public link to send to prospective employers (LinkedIn DM, warm outreach).
                                        Anonymized just like the marketplace. Expires in 30 days — revoke anytime.
                                    </p>
                                    {shareUrl ? (
                                        <div className="flex items-center gap-2">
                                            <input
                                                readOnly
                                                value={shareUrl}
                                                onFocus={(e) => e.target.select()}
                                                data-testid="passport-share-url"
                                                className="flex-1 bg-muted border border-border rounded-md px-2 py-1.5 text-xs font-mono truncate"
                                            />
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(shareUrl).then(() => {
                                                        setShareCopied(true);
                                                        toast.success("Copied.");
                                                        setTimeout(() => setShareCopied(false), 2500);
                                                    }).catch(() => toast.error("Clipboard blocked — copy manually"));
                                                }}
                                                data-testid="passport-share-copy-btn"
                                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-border hover:bg-muted shrink-0"
                                            >
                                                {shareCopied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                                            </button>
                                            <button
                                                onClick={revokeShareLink}
                                                disabled={shareBusy}
                                                data-testid="passport-share-revoke-btn"
                                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md text-rose-700 dark:text-rose-400 hover:bg-rose-500/10 shrink-0 disabled:opacity-60"
                                            >
                                                {shareBusy ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />} Revoke
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={mintShareLink}
                                            disabled={shareBusy}
                                            data-testid="passport-share-generate-btn"
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-sky-500/40 text-sky-700 dark:text-sky-400 hover:bg-sky-500/10 disabled:opacity-60"
                                        >
                                            {shareBusy ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
                                            {" "}Copy shareable preview link
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                        <DialogFooter>
                            <button
                                onClick={() => setPreviewOpen(false)}
                                className="text-sm px-4 py-1.5 rounded-md border border-border hover:bg-muted"
                                data-testid="passport-preview-close"
                            >
                                Close
                            </button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default TalentPassport;
